import { TextField } from '@nps.today/component-library';
import { FunctionComponent, useCallback, useMemo, useRef } from 'react';

const componentDataTestId = 'integer';

interface IntegerInputProps {
  label: string;
  value: number;
  defaultValue?: number | null;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  onChange: (v: number | string | null) => void;
  allowDecimal?: boolean;
  maxValue?: number;
  dataTestId: string;
}

export const IntegerInput: FunctionComponent<IntegerInputProps> = ({
  label,
  value,
  defaultValue = 0,
  error,
  helperText,
  disabled,
  required,
  onChange,
  allowDecimal = false,
  maxValue,
  dataTestId
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const disallowedKeys = useMemo(() => {
    const values = { e: 'e', E: 'E', '-': '-', '+': '+' };
    const keysMap = new Map(Object.entries(values));

    return keysMap;
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disallowedKeys.has(e.key)) {
        e.preventDefault();
      }
    },
    [disallowedKeys]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      let value = allowDecimal
        ? Number.parseFloat(e.target.value)
        : Number.parseInt(e.target.value);
      const valueExceedsMaxValue = maxValue !== undefined && value > maxValue;
      if (Number.isNaN(value)) {
        onChange('');
      } else if (valueExceedsMaxValue) {
        onChange(maxValue);
      } else {
        onChange(value);
      }
    },
    [onChange, allowDecimal, maxValue]
  );

  return (
    <TextField
      dataTestId={`${dataTestId}-${componentDataTestId}`}
      ref={inputRef}
      label={label}
      value={value}
      error={error}
      helperText={helperText}
      disabled={disabled}
      required={required}
      onPaste={(e) => {
        e.preventDefault();
      }}
      fullWidth
      type="number"
      onChange={handleChange}
      onBlur={(e) => {
        if (e.target.value === '') {
          onChange(defaultValue);
        }
      }}
      onKeyDown={handleKeyDown}
    />
  );
};
